// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { mergeWithCss } from "@turnerBlueprint/XDL/Utilities/mergeWithCss";
import React, { Children, FC, useState } from "react";

export interface CartLineContainerProps {
    extendedStyles?: CartLineContainerStyles;
    includeRightActions?: boolean;
    includeLeftActions?: boolean;
    isCondensed?: boolean;
    [key: string]: any;
}
interface OwnProps {
    Image: React.FC;
    LeftActions: React.FC;
    Description: React.FC;
    Price: React.FC;
    Qty: React.FC;
    Total: React.FC;
    Remove: React.FC;
    Actions: React.FC;
    Notes: React.FC;
}
export interface CartLineContainerStyles {
    container?: InjectableCss;
    image?: InjectableCss;
    leftActions?: InjectableCss;
    description?: InjectableCss;
    price?: InjectableCss;
    qty?: InjectableCss;
    total?: InjectableCss;
    actions?: InjectableCss;
    notes?: InjectableCss;
    remove?: InjectableCss;
}

export const cartlineContainerStyles: CartLineContainerStyles = {};

const names = {
    Image: "Image",
    LeftActions: "LeftActions",
    Description: "Description",
    Price: "Price",
    Qty: "Qty",
    Total: "Total",
    Remove: "Remove",
    Actions: "Actions",
    Notes: "Notes",
};

const Image: FC = () => null;
const LeftActions: FC = () => null;
const Description: FC = () => null;
const Price: FC = () => null;
const Qty: FC = () => null;
const Total: FC = () => null;
const Remove: FC = () => null;
const Actions: FC = () => null;
const Notes: FC = () => null;

const CartLineContainer: React.FC<CartLineContainerProps> & OwnProps = ({
    children,
    extendedStyles,
    includeLeftActions = false,
    includeRightActions = true,
    isCondensed = false,
}) => {
    const [styles] = useState(() => mergeWithCss(cartlineContainerStyles, extendedStyles));

    const { Image, LeftActions, Description, Price, Qty, Total, Remove, Actions, Notes } = React.useMemo(() => {
        const childrenArray = Children.toArray(children) as React.ReactElement[];
        const MappedChilds = childrenArray.reduce((results, current) => {
            const currentName = (current.type as React.NamedExoticComponent<any>)?.displayName;
            if (currentName) {
                results[currentName] = current;
            }
            return results;
        }, {} as Record<string, React.ReactElement>);
        const slots = Object.entries(names).reduce((results, [key, value], idx) => {
            results[value] = MappedChilds[value] || null;
            return results;
        }, {} as Record<string, React.ReactElement | null>);

        return slots;
    }, [children]);

    return (
        <StyledWrapper {...styles.container} {...{ includeRightActions, includeLeftActions }}>
            {includeLeftActions && (
                <StyledWrapper {...styles.leftActions}>{LeftActions?.props?.children}</StyledWrapper>
            )}
            <StyledWrapper {...styles.image} {...{ includeLeftActions }} {...Image?.props}>
                {Image?.props?.children}
            </StyledWrapper>
            <StyledWrapper {...styles.description} {...{ includeLeftActions }}>
                {Description?.props?.children}
            </StyledWrapper>
            <StyledWrapper {...styles.price} {...{ includeLeftActions }}>
                {Price?.props?.children}
            </StyledWrapper>
            <StyledWrapper {...styles.qty} {...{ includeLeftActions }}>
                {Qty?.props?.children}
            </StyledWrapper>
            <StyledWrapper {...styles.total} {...{ includeLeftActions }}>
                {Total?.props?.children}
            </StyledWrapper>
            {includeRightActions && (
                <StyledWrapper {...styles.remove} {...{ includeLeftActions }}>
                    {Remove?.props?.children}
                </StyledWrapper>
            )}
            <StyledWrapper {...styles.actions} {...{ includeLeftActions }}>
                {Actions?.props?.children}
            </StyledWrapper>
            <StyledWrapper {...styles.notes} {...{ includeLeftActions }}>
                {Notes?.props?.children}
            </StyledWrapper>
        </StyledWrapper>
    );
};

CartLineContainer.LeftActions = LeftActions;
CartLineContainer.Image = Image;
CartLineContainer.Description = Description;
CartLineContainer.Price = Price;
CartLineContainer.Qty = Qty;
CartLineContainer.Total = Total;
CartLineContainer.Remove = Remove;
CartLineContainer.Actions = Actions;
CartLineContainer.Notes = Notes;

CartLineContainer.LeftActions.displayName = names.LeftActions;
CartLineContainer.Image.displayName = names.Image;
CartLineContainer.Description.displayName = names.Description;
CartLineContainer.Price.displayName = names.Price;
CartLineContainer.Qty.displayName = names.Qty;
CartLineContainer.Total.displayName = names.Total;
CartLineContainer.Remove.displayName = names.Remove;
CartLineContainer.Actions.displayName = names.Actions;
CartLineContainer.Notes.displayName = names.Notes;

export default CartLineContainer;
